import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HiOutlinePlus, HiOutlineClock, HiOutlinePencilSquare, HiOutlineTrash } from 'react-icons/hi2';
import { Table } from 'flowbite-react';
import { getSchedulers, deleteScheduler } from '../../services/model/scheduler-service';
import PageContainer from "../../components/page-container";
import ConfirmDialog from "../../components/confirm-dialog/confirm-dialog";
import SchedulerSaveModal from './save';

function SchedulerListPage() {
  const { appId } = useParams();
  const [schedulers, setSchedulers] = useState([]);
  const [openCreateScheduler, setOpenCreateScheduler] = useState(false);
  const [editScheduler, setEditScheduler] = useState(null);
  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    errorMessage: ''
  });

  const [deletingData, setDeletingData] = useState(null);
  const [openDeleteConfirmDialog, setDeleteConfirmDialog] = useState(false);

  async function fetchSchedulers() {
    updateFormState(true, false, false);
    const data = await getSchedulers(appId);
    setSchedulers(data);
    updateFormState(false, false, false);
  }

  const handleDelete = (schedulerId) => {
    setDeletingData(schedulerId);
    setDeleteConfirmDialog(true);
  };

  const handleEdit = (scheduler) => {
    setEditScheduler(scheduler);
    setOpenCreateScheduler(true);
  };

  async function deleteSchedulerItem(status) {
    if (status) {
      updateFormState(true, false, false);
      await deleteScheduler(appId, deletingData);
      updateFormState(false, false, false);
      await fetchSchedulers();
    }
    setDeleteConfirmDialog(false);
    setDeletingData(null);
  }

  useEffect(() => {
    fetchSchedulers();
  }, [appId]);

  const handleOpenCreateScheduler = () => {
    setEditScheduler(null);
    setOpenCreateScheduler(true);
  };

  const handleOnCompletionCreateScheduler = (success) => {
    setOpenCreateScheduler(false);
    if (success) {
      fetchSchedulers();
    }
  };

  const updateFormState = (processing, success, failed, errorMessage) => {
    setFormState(Object.assign({}, { processing, success, failed, errorMessage }));
  };

  return (
    <PageContainer
      title="Schedulers"
      tooltip="Manage scheduled jobs for workflows"
      headerAction={
        <button
          type="button"
          onClick={handleOpenCreateScheduler}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-secondary hover:shadow-md active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <HiOutlinePlus className="h-4 w-4" />
          New Scheduler
        </button>
      }
    >
      {openCreateScheduler && (
        <SchedulerSaveModal
          appId={appId}
          scheduler={editScheduler}
          onCompletion={handleOnCompletionCreateScheduler}
        />
      )}

      {openDeleteConfirmDialog && (
        <ConfirmDialog
          open={openDeleteConfirmDialog}
          onCompletion={deleteSchedulerItem}
          title="Delete Confirmation"
          message="Are you sure you want to delete this scheduler? This action cannot be undone."
        />
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell className="bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Name
              </Table.HeadCell>
              <Table.HeadCell className="bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Target Workflow
              </Table.HeadCell>
              <Table.HeadCell className="bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Schedule
              </Table.HeadCell>
              <Table.HeadCell className="bg-gray-50/80">
                <span className="sr-only">Actions</span>
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {schedulers.length > 0 ? (
                schedulers.map((scheduler) => (
                  <Table.Row
                    key={scheduler.jobId}
                    className="border-b border-gray-100 bg-white transition-colors last:border-0 hover:bg-gray-50/70"
                  >
                    <Table.Cell className="whitespace-nowrap font-medium text-gray-900">
                      {scheduler.name}
                    </Table.Cell>
                    <Table.Cell className="text-gray-600">
                      {scheduler.targetId}
                    </Table.Cell>
                    <Table.Cell>
                      <span className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-xs text-gray-600">
                        <HiOutlineClock className="h-3.5 w-3.5" />
                        {scheduler.scheduleCron || scheduler.scheduleAt}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          aria-label={`Edit ${scheduler.name}`}
                          onClick={() => handleEdit(scheduler)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        >
                          <HiOutlinePencilSquare className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${scheduler.name}`}
                          onClick={() => handleDelete(scheduler.jobId)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                        >
                          <HiOutlineTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row className="bg-white">
                  <Table.Cell colSpan={4} className="py-14">
                    <div className="flex flex-col items-center justify-center gap-2 text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                        <HiOutlineClock className="h-6 w-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        No schedulers yet
                      </p>
                      <p className="text-xs text-gray-500">
                        Create one to run your workflows on a schedule.
                      </p>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </div>

    </PageContainer>
  );
}

export default SchedulerListPage;
